import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CollegeSelection.css';
import {
  Globe,
  ShieldCheck,
  Cpu,
  ArrowLeft,
  Search,
  Building2,
  MapPin,
  CheckCircle2,
  Send,
  Sparkles,
  School,
  Users,
} from 'lucide-react';

const CollegeSelection = ({ formData, onPostJob, onBack, isRecruiter }) => {
  const [colleges, setColleges] = useState([]);
  const [selectedColleges, setSelectedColleges] = useState([]);
  const [postToCareers, setPostToCareers] = useState(false);
  const [isLoadingColleges, setIsLoadingColleges] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [isVerifyingAI, setIsVerifyingAI] = useState(false);
  const [isMismatch, setIsMismatch] = useState(false);
  const [overridePremium, setOverridePremium] = useState(false);

  const isPremiumJob = isRecruiter ? (isMismatch && !overridePremium) : false;

  // <--- CHANGED: Now properly handles the single Country object
  const selectedCountries = formData.targetCountry ? [formData.targetCountry.value] : [];
  const ctryString = formData.targetCountry ? formData.targetCountry.value : '';

  useEffect(() => {
    const verifyLocation = async () => {
      if (!isRecruiter) {
        setIsMismatch(false);
        setIsVerifyingAI(false);
        return;
      }

      setIsVerifyingAI(true);
      try {
        const loc = formData.location || '';

        if (!loc || selectedCountries.length === 0) {
          setIsMismatch(false);
          setIsVerifyingAI(false);
          return;
        }

        const res = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/college/verify-location`,
          {
            location: loc,
            country: ctryString,
          }
        );

        setIsMismatch(!res.data.isMatch);
      } catch (error) {
        console.error('AI check failed, using basic text fallback', error);
        const locLower = (formData.location || '').toLowerCase();
        
        const fallbackMatch = selectedCountries.some(
          c => locLower.includes(c.toLowerCase()) || c.toLowerCase().includes(locLower)
        );
        setIsMismatch(!fallbackMatch);
      } finally {
        setIsVerifyingAI(false);
      }
    };
    verifyLocation();
  }, [formData.location, formData.targetCountry, isRecruiter]);

  useEffect(() => {
    const fetchColleges = async () => {
      try {
        setIsLoadingColleges(true);
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/colleges`);

        let filteredByCountry = response.data;
        if (selectedCountries.length > 0) {
          filteredByCountry = response.data.filter(c => selectedCountries.includes(c.country));
        }

        setColleges(filteredByCountry);
      } catch (error) {
        console.error('Failed to fetch colleges:', error);
      } finally {
        setIsLoadingColleges(false);
      }
    };

    if (!isVerifyingAI) {
      fetchColleges();
    }
  }, [formData.targetCountry, isVerifyingAI]);

  const handleSelect = (collegeId) => {
    setSelectedColleges(prev =>
      prev.includes(collegeId) ? prev.filter(id => id !== collegeId) : [...prev, collegeId]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedColleges(colleges.map(c => c._id || c.id));
    else setSelectedColleges([]);
  };

  const handleFinalPost = () => {
    if (selectedColleges.length === 0 && !postToCareers && !isPremiumJob) {
      alert("Please select at least one College OR check 'Post to Careers Page'.");
      return;
    }
    onPostJob(selectedColleges, postToCareers, isPremiumJob);
  };

  const filteredColleges = colleges.filter(c =>
    c.collegeName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleName = () => {
    if (!formData.role) return 'N/A';
    if (typeof formData.role === 'string') return formData.role;
    return formData.role.label || formData.role.value || 'N/A';
  };

  // <--- CHANGED: Simply prints the single country label
  const getCountryNames = () => {
    return formData.targetCountry ? formData.targetCountry.label : 'All Countries';
  };

  if (isVerifyingAI) {
    return (
      <div className="cs-root">
        <div className="cs-bg-blob cs-blob-1" />
        <div className="cs-bg-blob cs-blob-2" />
        <div className="cs-verifying">
          <div className="cs-verifying-card">
            <div className="cs-verifying-icon">
              <Cpu size={40} />
            </div>
            <h2>AI Location Verification</h2>
            <p>
              Checking if <strong>{formData.location}</strong> matches the selected country…
            </p>
            <div className="cs-verifying-bar">
              <div className="cs-verifying-bar-fill" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cs-root">
      <div className="cs-bg-blob cs-blob-1" />
      <div className="cs-bg-blob cs-blob-2" />

      <div className="cs-header">
        <button className="cs-back-btn" onClick={onBack}>
          <ArrowLeft size={16} /> Back
        </button>
        <div className="cs-header-info">
          <div className="cs-title-icon">
            <School size={24} />
          </div>
          <div>
            <h1 className="cs-title">Select Target Colleges</h1>
            <p className="cs-subtitle">Step 2 of 2 — Choose where to publish your job</p>
          </div>
        </div>

        <div className="cs-stepper">
          <div className="cs-step done">
            <span className="cs-step-num">
              <CheckCircle2 size={14} />
            </span>
            <span className="cs-step-label">Job Details</span>
          </div>
          <div className="cs-step-line" />
          <div className="cs-step active">
            <span className="cs-step-num">2</span>
            <span className="cs-step-label">Select Colleges</span>
          </div>
        </div>
      </div>

      {isMismatch && isRecruiter && (
        <div className={`cs-premium-banner ${isPremiumJob ? 'cs-premium-active' : 'cs-premium-override'}`}>
          <div className="cs-premium-left">
            <div className="cs-premium-icon">
              <Globe size={22} />
            </div>
            <div className="cs-premium-text">
              <h3>
                {isPremiumJob ? 'Global Premium Job Detected' : 'Standard Job Override Active'}
                {isPremiumJob && (
                  <span className="cs-premium-tag">
                    <Sparkles size={11} /> PREMIUM
                  </span>
                )}
              </h3>
              <p>
                We noticed the job location (<strong>{formData.location}</strong>) differs from
                target country (<strong>{getCountryNames()}</strong>).{' '}
                {isPremiumJob
                  ? 'This job will automatically bypass private college feeds and be placed in the Premium Global Database.'
                  : 'This job will be posted to the selected colleges normally.'}
              </p>
            </div>
          </div>

          <label className="cs-override-toggle">
            <input
              type="checkbox"
              checked={overridePremium}
              onChange={(e) => setOverridePremium(e.target.checked)}
            />
            <span className="cs-override-content">
              <ShieldCheck size={16} /> Override: Post as Standard Free Job
            </span>
          </label>
        </div>
      )}

      {/* ── CHANGED: Public Posting Toggle (Freely toggleable now) ── */}
      <div
        className={`cs-careers-card ${postToCareers ? 'cs-careers-active' : ''}`}
        onClick={() => setPostToCareers(!postToCareers)}
      >
        <div className="cs-careers-left">
          <div className="cs-careers-icon">
            <Globe size={20} />
          </div>
          <div className="cs-careers-text">
            <h4>Post to Public Careers Page</h4>
            <p>If checked, this job will also be visible to anyone visiting the public website.</p>
          </div>
        </div>

        <div className="cs-switch-wrap">
          <input
            type="checkbox"
            id="careersToggle"
            className="cs-switch-input"
            checked={postToCareers}
            onChange={(e) => {
              e.stopPropagation();
              setPostToCareers(e.target.checked);
            }}
          />
          <label
            htmlFor="careersToggle"
            className="cs-switch"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="cs-switch-slider">
              <span className="cs-switch-knob" />
            </span>
          </label>
        </div>
      </div>

      {(!isPremiumJob || !isRecruiter) && (
        <div className="cs-list-card">
          <div className="cs-summary">
            <div className="cs-summary-icon">
              <Building2 size={18} />
            </div>
            <p>
              Posting for <strong>{getRoleName()}</strong> at{' '}
              <strong>{formData.companyName || 'N/A'}</strong>. Showing colleges in{' '}
              <strong>{getCountryNames()}</strong>.
            </p>
          </div>

          <div className="cs-controls">
            <div className="cs-search-wrap">
              <Search size={16} className="cs-search-icon" />
              <input
                type="text"
                placeholder="Search for a college…"
                className="cs-search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="cs-select-all">
              <label className="cs-checkbox">
                <input
                  type="checkbox"
                  id="selectAll"
                  onChange={handleSelectAll}
                  checked={colleges.length > 0 && selectedColleges.length === colleges.length}
                  disabled={colleges.length === 0}
                />
                <span>Select All</span>
              </label>
              {selectedColleges.length > 0 && (
                <span className="cs-count-badge">
                  <Users size={13} /> {selectedColleges.length} selected
                </span>
              )}
            </div>
          </div>

          {isLoadingColleges ? (
            <div className="cs-loading">
              <Cpu size={28} className="cs-loading-icon" />
              <span>Loading colleges…</span>
            </div>
          ) : filteredColleges.length === 0 ? (
            <div className="cs-empty">
              <School size={36} className="cs-empty-icon" />
              <p>No colleges found for the selected country</p>
              <span>Try adjusting your search or country selection</span>
            </div>
          ) : (
            <div className="cs-college-grid">
              {filteredColleges.map((college) => {
                const cId = college._id || college.id;
                const isSelected = selectedColleges.includes(cId);
                return (
                  <label
                    key={cId}
                    htmlFor={cId}
                    className={`cs-college-card ${isSelected ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      id={cId}
                      checked={isSelected}
                      onChange={() => handleSelect(cId)}
                    />
                    <div className="cs-college-check">
                      <CheckCircle2 size={18} />
                    </div>
                    <div className="cs-college-info">
                      <span className="cs-college-name">{college.collegeName}</span>
                      <span className="cs-college-location">
                        <MapPin size={11} /> {college.location}
                      </span>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="cs-actions">
        <button type="button" className="cs-btn-secondary" onClick={onBack}>
          <ArrowLeft size={16} /> Back
        </button>
        <button
          type="button"
          className={`cs-btn-primary ${(isPremiumJob && isRecruiter) ? 'cs-btn-premium' : ''}`}
          onClick={handleFinalPost}
        >
          {isPremiumJob && isRecruiter ? (
            <>
              <Sparkles size={15} /> Publish Premium Job
            </>
          ) : postToCareers ? (
            <>
              <Send size={15} /> Post Job (Public & Private)
            </>
          ) : (
            <>
              <Send size={15} /> Post Job
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CollegeSelection;